import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Search, Download, Eye, QrCode, Plus, CheckCircle, AlertTriangle, XCircle, X, FileDown, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/services/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';

const StatusBadge = ({ statut }) => {
  const config = {
    'Délivrée': { variant: 'default', icon: CheckCircle, className: 'bg-green-100 text-green-800' },
    'En signature': { variant: 'secondary', icon: AlertTriangle, className: 'bg-blue-100 text-blue-800' },
    'À valider HSE': { variant: 'secondary', icon: AlertTriangle, className: 'bg-orange-100 text-orange-800' },
    'Brouillon': { variant: 'outline', icon: null, className: '' },
    'Révoquée': { variant: 'destructive', icon: XCircle, className: '' },
    'Échue': { variant: 'destructive', icon: XCircle, className: '' },
    'Archivée': { variant: 'secondary', icon: Archive, className: 'bg-gray-100 text-gray-600' }
  };
  const { icon: Icon, className } = config[statut] || { className: '' };
  return (
    <Badge className={className}>
      {Icon && <Icon className="h-3 w-3 mr-1" />}
      {statut}
    </Badge>
  );
};

export default function Autorisations() {
  const [autorisations, setAutorisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('all');
  const [showArchives, setShowArchives] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);

  useEffect(() => {
    loadAutorisations();
  }, [showArchives]);

  const loadAutorisations = async () => {
    try {
      setLoading(true);
      const params = showArchives ? { include_archives: true } : {};
      const data = await api.getAutorisations(params);
      setAutorisations(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des autorisations');
    } finally {
      setLoading(false);
    }
  };

  const filteredAutorisations = autorisations.filter(auth => {
    const matchesSearch = !searchTerm ||
      auth.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      auth.collaborateur?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      auth.collaborateur?.prenom?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatut = statutFilter === 'all' || auth.statut === statutFilter;
    return matchesSearch && matchesStatut;
  });

  const stats = {
    active: autorisations.filter(a => a.statut === 'Délivrée').length,
    enCours: autorisations.filter(a => ['Brouillon', 'À valider HSE', 'En signature', 'À signer'].includes(a.statut)).length,
    echues: autorisations.filter(a => a.statut === 'Échue' || a.statut === 'Révoquée').length,
    archived: autorisations.filter(a => a.statut === 'Archivée').length
  };

  const handleShowQrCode = async (auth) => {
    try {
      setLoadingQr(true);
      setQrDialogOpen(true);
      const data = await api.getAutorisationQRCode(auth.id);
      setQrData({ ...data, numero: auth.numero });
    } catch (error) {
      console.error('Error loading QR code:', error);
      toast.error('Erreur lors du chargement du QR code');
      setQrDialogOpen(false);
    } finally {
      setLoadingQr(false);
    }
  };

  const handleDownloadCertificate = async (auth) => {
    try {
      toast.info('Génération du certificat en cours...');
      const result = await api.generateAutorisationDocument(auth.id);
      if (result.pdf_base64) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${result.pdf_base64}`;
        link.download = `certificat-${auth.numero || 'autorisation'}.pdf`;
        link.click();
        toast.success('Certificat téléchargé');
      } else if (result.html) {
        // Open HTML in new window for printing
        const win = window.open('', '_blank');
        win.document.write(result.html);
        win.document.close();
        toast.success('Document généré - Vous pouvez l\'imprimer (Ctrl+P)');
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error('Erreur lors de la génération du certificat');
    }
  };

  const handleCopyLink = () => {
    if (qrData?.verification_url) {
      navigator.clipboard.writeText(qrData.verification_url);
      toast.success('Lien copié !');
    }
  };

  const handleDownloadQr = () => {
    if (qrData?.qr_code_base64) {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${qrData.qr_code_base64}`;
      link.download = `qrcode-${qrData.numero || 'autorisation'}.png`;
      link.click();
      toast.success('QR Code téléchargé');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="w-7 h-7 text-blue-600" />
            Autorisations internes
          </h1>
          <p className="text-gray-500 mt-1">
            {autorisations.length} autorisation{autorisations.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="archives"
              checked={showArchives}
              onCheckedChange={setShowArchives}
            />
            <Label htmlFor="archives" className="text-sm flex items-center gap-1 cursor-pointer">
              <Archive className="h-4 w-4" />
              Voir les archives
            </Label>
          </div>
          <Link to="/nouvelle-autorisation">
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              Nouvelle autorisation
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Actives</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En cours</p>
                <p className="text-2xl font-bold">{stats.enCours}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${showArchives ? 'bg-gray-100' : 'bg-red-100'}`}>
                {showArchives ? (
                  <Archive className="h-5 w-5 text-gray-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">{showArchives ? 'Archivées' : 'Échues/Révoquées'}</p>
                <p className="text-2xl font-bold">{showArchives ? stats.archived : stats.echues}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par numéro, collaborateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="Délivrée">Délivrées</SelectItem>
                <SelectItem value="En signature">En signature</SelectItem>
                <SelectItem value="À valider HSE">À valider HSE</SelectItem>
                <SelectItem value="Brouillon">Brouillons</SelectItem>
                <SelectItem value="Échue">Échues</SelectItem>
                <SelectItem value="Révoquée">Révoquées</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredAutorisations.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Aucune autorisation trouvée</p>
              <Link to="/nouvelle-autorisation">
                <Button variant="link">Créer une autorisation</Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Collaborateur</TableHead>
                  <TableHead>Catégories</TableHead>
                  <TableHead>Délivrée le</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAutorisations.map((auth) => (
                  <TableRow key={auth.id}>
                    <TableCell className="font-mono text-sm">{auth.numero}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {auth.collaborateur?.prenom} {auth.collaborateur?.nom}
                        </p>
                        <p className="text-sm text-gray-500">{auth.collaborateur?.secteur}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {auth.categories?.map((cat, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{auth.date_delivrance ? new Date(auth.date_delivrance).toLocaleDateString('fr-FR') : '-'}</TableCell>
                    <TableCell>{auth.date_fin ? new Date(auth.date_fin).toLocaleDateString('fr-FR') : '-'}</TableCell>
                    <TableCell><StatusBadge statut={auth.statut} /></TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Link to={`/demandes/${auth.id}`}>
                          <Button variant="ghost" size="icon" title="Voir détails">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        {auth.statut === 'Délivrée' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title="Télécharger certificat"
                              onClick={() => handleDownloadCertificate(auth)}
                            >
                              <FileDown className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title="QR Code"
                              onClick={() => handleShowQrCode(auth)}
                            >
                              <QrCode className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">
              Autorisation {qrData?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4 py-4">
            {loadingQr ? (
              <div className="h-48 w-48 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : qrData?.qr_code_base64 ? (
              <>
                <img
                  src={`data:image/png;base64,${qrData.qr_code_base64}`}
                  alt="QR Code"
                  className="w-48 h-48"
                />
                <p className="text-sm text-muted-foreground text-center">
                  Code de vérification: <span className="font-mono font-bold">{qrData.verification_code}</span>
                </p>
                <div className="flex gap-2 w-full">
                  <Button variant="outline" className="flex-1" onClick={handleCopyLink}>
                    Copier le lien
                  </Button>
                  <Button className="flex-1" onClick={handleDownloadQr}>
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Scannez ce code pour vérifier l'authenticité de l'autorisation
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">Erreur lors du chargement du QR code</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
